/**
 * Client-side Temporal polyfill installer — dynamic-imports `@js-temporal/polyfill`
 * only when the runtime doesn't already ship `Temporal` natively (Firefox 139+,
 * recent Chrome/Edge channels). Returns a Promise that resolves once
 * `globalThis.Temporal` is installed.
 *
 * Call once at app bootstrap before any module that touches `Temporal` at
 * runtime. On native-Temporal browsers only the (tiny, already-bundled)
 * extension modules are imported and applied to the native prototypes — the
 * polyfill chunk is never fetched. Otherwise the polyfill is fetched from
 * its own async chunk (Rollup code-splits the dynamic import); subsequent
 * calls short-circuit on the cached promise. Either way the promise resolves
 * only after the PowerDuck/Inviton symbol extensions are applied to whatever
 * implementation `globalThis.Temporal` ends up being.
 */

export class TemporalPolyfillInstaller {
	private static installed: Promise<void> | null = null;

	static install(): Promise<void> {
		if (this.installed != null) {
			return this.installed;
		}

		if ((globalThis as any).Temporal != null) {
			// Native Temporal: no polyfill chunk needed, but the symbol extensions must
			// still be applied to the native prototypes — same as the polyfill branch below.
			this.installed = Promise.all([
				import('@powerduck/common/extensions/temporal-extensions'),
				import('./extensions/inviton-temporal-extensions'),
			]).then(([
				extensionsMod,
				invitonExtensionsMod,
			]) => {
				extensionsMod.applyTemporalExtensions();
				invitonExtensionsMod.applyInvitonTemporalExtensions();
			});

			return this.installed;
		}

		this.installed = Promise.all([
			import('@js-temporal/polyfill'),
			import('@powerduck/common/extensions/temporal-extensions'),
			import('./extensions/inviton-temporal-extensions'),
		]).then(([
			polyfillMod,
			extensionsMod,
			invitonExtensionsMod,
		]) => {
			if ((globalThis as any).Temporal == null) {
				(globalThis as any).Temporal = polyfillMod.Temporal;
			}

			extensionsMod.applyTemporalExtensions();
			invitonExtensionsMod.applyInvitonTemporalExtensions();
		});

		return this.installed;
	}
}